import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useAppContextNonNull } from "@contexts/AppContext";
import UiContext from "../../../contexts/UiContext";
import { buildRestUrl, buildRestUrlWithQuery, buildAuthHeadersForFetch } from "../../../services/RestService";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import IBaseOnGetCsvDataDTO from "../../../models/DTO/IBaseOnGetCsvDataDTO";
import IOnGetDailyRouteAuditDTO, { IAuditAttachment, IAuditEvent, IAuditStop } from "../../../models/DTO/IOnGetDailyRouteAuditDTO";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import UIService from "../../../services/UIService";
import { useGoogleMaps } from "@hooks/shared/useGoogleMaps";
import {
    buildRouteMarkers,
    buildStaticMapUrl,
    buildStopMarkers,
    describeEventMetadata,
    eventLabelKey,
    IEventMetadataFormatter,
    findStop,
    flagLabelKey,
    formatAuditTime,
    formatDistance,
    formatGeoPoint,
    formatMinutes,
    isSevereFlag,
    sortedRouteTimeline,
    IStaticMapMarker,
} from "../../../services/DailyRouteAuditReportService";
import HelperService from "../../../services/HelperService";
import CurrenciesEnum from "../../../models/CurrenciesEnum";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwButton from "../../shared/OwButton/OwButton";
import OwPrintButton from "../../shared/OwPrintButton/OwPrintButton";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import "./DailyRouteAuditReport.scss";

type AuditCsvType = "stops" | "events";

interface IViewOptions {
    showMaps: boolean;
    showPhotos: boolean;
    showEvents: boolean;
    showOrderLines: boolean;
    /** Customer-facing copy: hides driver notes, internal flags and the event timeline. */
    customerCopy: boolean;
}

const DEFAULT_VIEW_OPTIONS: IViewOptions = {
    showMaps: true,
    showPhotos: true,
    showEvents: true,
    showOrderLines: true,
    customerCopy: false,
};

// ------------------------------------------------------------------ small pieces

/** One labelled value in a facts grid; renders an em dash when the value is empty. */
const Fact = ({
    label,
    value,
    sub,
    wide,
    raw,
}: {
    label: string;
    value?: React.ReactNode;
    sub?: React.ReactNode;
    wide?: boolean;
    raw?: boolean;
}) => (
    <div className={`fact${wide ? " fact-wide" : ""}`}>
        <span className="fact-label">{raw ? label : <OwLabel title={label} />}</span>
        <span className="fact-value">{value === undefined || value === null || value === "" ? "—" : value}</span>
        {sub ? <span className="fact-sub">{sub}</span> : null}
    </div>
);

/** Table cells show a dash rather than an empty cell when a value is not recorded. */
const orDash = (value: string): string => value || "—";

/** Coloured pill for a stop status (pending / arrived / completed / failed / skipped). */
const StatusPill = ({ status }: { status: string }) => (
    <span className={`stop-status-pill status-${status}`}>
        <OwLabel title={status} />
    </span>
);

/** Audit flags for a stop as small badges; severe flags are rendered in red. */
const FlagBadges = ({ flags }: { flags: IAuditStop["flags"] }) => {
    if (!flags.length) {
        return null;
    }

    return (
        <span className="flag-badges">
            {flags.map((flag) => (
                <span key={flag} className={`flag-badge${isSevereFlag(flag) ? " severe" : ""}`}>
                    <OwLabel title={flagLabelKey(flag)} />
                </span>
            ))}
        </span>
    );
};

/** Google Static Maps image for a set of pins, with the customer / arrived / completed legend. */
const AuditStaticMap = ({
    markers,
    apiKey,
    size,
    legend,
}: {
    markers: IStaticMapMarker[];
    apiKey: string;
    size?: string;
    legend: boolean;
}) => {
    const url = buildStaticMapUrl(markers, apiKey, size);

    return (
        <div className="audit-map">
            {url ? (
                <img src={url} alt="Map" />
            ) : (
                <div className="map-missing">
                    <OwLabel title="audit_no_map_locations" />
                </div>
            )}
            {legend && url ? (
                <div className="map-legend">
                    <span>
                        <span className="legend-pin customer" />
                        <OwLabel title="audit_legend_customer" />
                    </span>
                    <span>
                        <span className="legend-pin arrived" />
                        <OwLabel title="audit_legend_arrived" />
                    </span>
                    <span>
                        <span className="legend-pin completed" />
                        <OwLabel title="audit_legend_completed" />
                    </span>
                </div>
            ) : null}
        </div>
    );
};

/** Capture time, upload time and geotag of a photo or signature, in the report timezone. */
const AttachmentTime = ({ attachment, timezone }: { attachment: IAuditAttachment; timezone: string }) => (
    <>
        {attachment.captured_at ? (
            <div>
                <OwLabel title="captured_at" />: {formatAuditTime(attachment.captured_at, timezone)}
            </div>
        ) : null}
        <div>
            <OwLabel title="uploaded_at" />: {formatAuditTime(attachment.uploaded_at, timezone)}
        </div>
        {attachment.location ? (
            <div>
                <OwLabel title="gps" />: {formatGeoPoint(attachment.location)}
            </div>
        ) : null}
    </>
);

/**
 * Chronological table of audit events. With `stopLookup` (route-level timeline) each row also
 * names its stop; without it (per-stop card) the stop column is omitted.
 */
const EventsTable = ({
    events,
    timezone,
    stopLookup,
    metadataFormatter,
}: {
    events: IAuditEvent[];
    timezone: string;
    stopLookup?: Map<number, IAuditStop>;
    metadataFormatter: IEventMetadataFormatter;
}) => {
    if (!events.length) {
        return (
            <div className="proof-missing">
                <OwLabel title="audit_no_events_recorded" />
            </div>
        );
    }

    return (
        <table className="audit-table">
            <thead>
                <tr>
                    <th>
                        <OwLabel title="time" />
                    </th>
                    <th>
                        <OwLabel title="event" />
                    </th>
                    {stopLookup ? (
                        <th>
                            <OwLabel title="stop" />
                        </th>
                    ) : null}
                    <th>
                        <OwLabel title="status" />
                    </th>
                    <th>
                        <OwLabel title="reason" />
                    </th>
                    <th>
                        <OwLabel title="gps" />
                    </th>
                    <th>
                        <OwLabel title="by" />
                    </th>
                    <th>
                        <OwLabel title="source" />
                    </th>
                    <th>
                        <OwLabel title="detail" />
                    </th>
                </tr>
            </thead>
            <tbody>
                {events.map((event) => {
                    const stop = stopLookup && event.stop_id != null ? stopLookup.get(event.stop_id) : undefined;
                    const recordedDiffers =
                        event.event_at && event.recorded_at && Math.abs(Date.parse(event.recorded_at) - Date.parse(event.event_at)) > 60000;

                    return (
                        <tr key={event.guid}>
                            <td style={{ whiteSpace: "nowrap" }}>
                                {formatAuditTime(event.event_at, timezone)}
                                {recordedDiffers ? (
                                    <div className="fact-sub" style={{ fontSize: "0.72rem", opacity: 0.7 }}>
                                        <OwLabel title="audit_recorded_at" />: {formatAuditTime(event.recorded_at, timezone)}
                                    </div>
                                ) : null}
                            </td>
                            <td>
                                <OwLabel title={eventLabelKey(event.event_type)} />
                            </td>
                            {stopLookup ? (
                                <td>
                                    {stop ? (
                                        `#${stop.display_seq} ${stop.customer_name || ""}`
                                    ) : typeof event.metadata?.stop_id === "number" ? (
                                        // The stop row was deleted after the event; the FK is nulled but metadata keeps its id.
                                        <OwLabel title="audit_removed_stop" />
                                    ) : (
                                        ""
                                    )}
                                </td>
                            ) : null}
                            <td>
                                {event.from_status || event.to_status ? (
                                    <>
                                        {event.from_status ? <OwLabel title={event.from_status} /> : null}
                                        {event.from_status && event.to_status ? " → " : null}
                                        {event.to_status ? <OwLabel title={event.to_status} /> : null}
                                    </>
                                ) : null}
                            </td>
                            <td>{event.status_reason || ""}</td>
                            <td style={{ whiteSpace: "nowrap" }}>{formatGeoPoint(event.location)}</td>
                            <td>{event.user_name || ""}</td>
                            <td>{event.source}</td>
                            <td>{describeEventMetadata(event, metadataFormatter)}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

// ------------------------------------------------------------------ stop card

/**
 * One stop's full audit record: facts, GPS-vs-customer map, proof of delivery, van-sell lines
 * and payments, driver notes and its event timeline. Internal-only sections are hidden in
 * Customer Copy mode.
 */
const StopCard = ({
    stop,
    audit,
    options,
    apiKey,
    routeGuid,
    singleStop,
    metadataFormatter,
}: {
    stop: IAuditStop;
    audit: IOnGetDailyRouteAuditDTO;
    options: IViewOptions;
    apiKey: string;
    routeGuid: string;
    singleStop: boolean;
    metadataFormatter: IEventMetadataFormatter;
}) => {
    const tz = audit.timezone;
    const internal = !options.customerCopy;
    const markers = buildStopMarkers(stop);
    const far = stop.distance_from_customer_m != null && stop.distance_from_customer_m > audit.far_from_customer_threshold_m;

    return (
        <div className="stop-card" id={`stop-${stop.stop_id}`}>
            <div className="stop-card-header">
                <span className="stop-number">#{stop.display_seq}</span>
                <span className="stop-customer">{stop.customer_name || "—"}</span>
                {stop.order?.name ? (
                    <span className="stop-order">
                        <OwLabel title="order" /> {stop.order.name}
                        {stop.order.invoice_no ? ` · ${stop.order.invoice_no}` : ""}
                    </span>
                ) : null}
                <StatusPill status={stop.status} />
                {stop.is_van_sell ? (
                    <span className="stop-status-pill">
                        <OwLabel title="van_sell" />
                    </span>
                ) : null}
                {stop.is_cold_call ? (
                    <span className="stop-status-pill">
                        <OwLabel title="cold_call" />
                    </span>
                ) : null}
                {internal ? <FlagBadges flags={stop.flags} /> : null}
                <span className="stop-card-actions d-print-none">
                    {singleStop ? (
                        <Link to={`/routes/daily/${routeGuid}/audit`}>
                            <OwLabel title="audit_view_full_route" />
                        </Link>
                    ) : (
                        <Link to={`/routes/daily/${routeGuid}/audit?stop=${stop.stop_id}`}>
                            <OwLabel title="audit_view_single_stop" />
                        </Link>
                    )}
                </span>
            </div>

            <div className="stop-card-body">
                <div>
                    <div className="stop-facts">
                        <Fact label="ship_to" wide value={stop.ship_to?.formatted || "—"} sub={stop.ship_to?.phone || undefined} />
                        <Fact label="status" value={<StatusPill status={stop.status} />} sub={stop.status_reason || undefined} />
                        <Fact label="estimated_arrival" value={formatAuditTime(stop.estimated_arrival_time, tz)} />
                        <Fact
                            label="arrived_at"
                            value={formatAuditTime(stop.arrived_at, tz)}
                            sub={
                                stop.arrived_location ? (
                                    formatGeoPoint(stop.arrived_location)
                                ) : stop.arrived_at ? (
                                    <OwLabel title="audit_no_gps_fix" />
                                ) : undefined
                            }
                        />
                        <Fact
                            label="completed_at"
                            value={formatAuditTime(stop.completed_at, tz)}
                            sub={
                                stop.completed_location ? (
                                    formatGeoPoint(stop.completed_location)
                                ) : stop.completed_at ? (
                                    <OwLabel title="audit_no_gps_fix" />
                                ) : undefined
                            }
                        />
                        <Fact
                            label="audit_distance_from_customer"
                            value={
                                stop.distance_from_customer_m != null ? (
                                    <span style={far ? { color: "#c0392b", fontWeight: 600 } : undefined}>
                                        {formatDistance(stop.distance_from_customer_m)}
                                    </span>
                                ) : undefined
                            }
                            sub={stop.ship_to && !stop.ship_to.location ? <OwLabel title="audit_flag_customer_not_geocoded" /> : undefined}
                        />
                        <Fact
                            label="audit_eta_variance"
                            value={stop.eta_variance_minutes != null ? formatMinutes(stop.eta_variance_minutes) : undefined}
                        />
                        <Fact
                            label="audit_time_on_site"
                            value={formatMinutes(stop.dwell_minutes)}
                            sub={
                                stop.planned_service_minutes != null ? (
                                    <>
                                        <OwLabel title="audit_planned_service" />: {formatMinutes(stop.planned_service_minutes)}
                                    </>
                                ) : undefined
                            }
                        />
                        <Fact label="signed_by" value={stop.signed_by_name || undefined} />
                        <Fact
                            label="audit_signature_time"
                            value={formatAuditTime(stop.signature?.captured_at || stop.signature?.uploaded_at || null, tz)}
                        />
                        {internal && stop.was_reopt_overridden ? (
                            <Fact label="audit_flag_reopt_override" value={<OwLabel title="yes" />} />
                        ) : null}
                        {stop.order?.shipping_instructions ? (
                            <Fact label="shipping_instructions" wide value={stop.order.shipping_instructions} />
                        ) : null}
                    </div>

                    {internal && stop.driver_notes ? (
                        <div className="proof-section">
                            <div className="proof-title">
                                <OwLabel title="driver_notes" />
                            </div>
                            <div className="driver-notes">{stop.driver_notes}</div>
                        </div>
                    ) : null}

                    {options.showOrderLines && stop.order && stop.order.lines.length > 0 ? (
                        <div className="proof-section">
                            <div className="proof-title">
                                <OwLabel title="items" /> ({stop.order.line_count})
                            </div>
                            <table className="audit-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <OwLabel title="sku" />
                                        </th>
                                        <th>
                                            <OwLabel title="item" />
                                        </th>
                                        <th className="num">
                                            <OwLabel title="qty" />
                                        </th>
                                        <th className="num">
                                            <OwLabel title="shipped" />
                                        </th>
                                        {audit.van_sell_enabled ? (
                                            <th className="num">
                                                <OwLabel title="total" />
                                            </th>
                                        ) : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    {stop.order.lines.map((line, index) => (
                                        <tr key={`${line.sku}-${index}`}>
                                            <td>{line.sku || ""}</td>
                                            <td>{line.name || ""}</td>
                                            <td className="num">{line.qty}</td>
                                            <td className="num">{line.qty_shipped ?? ""}</td>
                                            {audit.van_sell_enabled ? (
                                                <td className="num">{line.total != null ? line.total.toFixed(2) : ""}</td>
                                            ) : null}
                                        </tr>
                                    ))}
                                </tbody>
                                {audit.van_sell_enabled && stop.order.grand_total != null ? (
                                    <tfoot>
                                        <tr>
                                            <td colSpan={4} className="num">
                                                <OwLabel title="total" />
                                            </td>
                                            <td className="num">{stop.order.grand_total.toFixed(2)}</td>
                                        </tr>
                                    </tfoot>
                                ) : null}
                            </table>
                        </div>
                    ) : null}

                    {audit.van_sell_enabled && stop.payments.length > 0 ? (
                        <div className="proof-section">
                            <div className="proof-title">
                                <OwLabel title="payments" />
                            </div>
                            <table className="audit-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <OwLabel title="type" />
                                        </th>
                                        <th className="num">
                                            <OwLabel title="amount" />
                                        </th>
                                        <th>
                                            <OwLabel title="reference" />
                                        </th>
                                        <th>
                                            <OwLabel title="received_at" />
                                        </th>
                                        <th>
                                            <OwLabel title="status" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stop.payments.map((payment) => (
                                        <tr key={payment.payment_guid}>
                                            <td>{payment.payment_type}</td>
                                            <td className="num">
                                                {payment.is_refund ? "-" : ""}
                                                {payment.amount.toFixed(2)}
                                            </td>
                                            <td>{payment.reference || ""}</td>
                                            <td>{formatAuditTime(payment.received_at, tz)}</td>
                                            <td>
                                                <OwLabel
                                                    title={payment.is_refund ? "refund" : payment.is_captured ? "captured" : "pending"}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td>
                                            <OwLabel title="total" />
                                        </td>
                                        <td className="num">{stop.payments_total.toFixed(2)}</td>
                                        <td colSpan={3} />
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    ) : null}
                </div>

                <div>
                    {options.showMaps ? <AuditStaticMap markers={markers} apiKey={apiKey} size="600x340" legend={true} /> : null}

                    <div className="proof-section">
                        <div className="proof-title">
                            <OwLabel title="signature" />
                        </div>
                        {stop.signature ? (
                            <div className="signature-block">
                                {stop.signature.signed_url ? <img src={stop.signature.signed_url} alt="Signature" /> : null}
                                <div className="signature-meta">
                                    {stop.signed_by_name ? (
                                        <div>
                                            <OwLabel title="signed_by" />: <strong>{stop.signed_by_name}</strong>
                                        </div>
                                    ) : null}
                                    <AttachmentTime attachment={stop.signature} timezone={tz} />
                                </div>
                            </div>
                        ) : (
                            <div className="proof-missing">
                                <OwLabel title="audit_no_signature_captured" />
                            </div>
                        )}
                        {internal && stop.prior_signatures.length > 0 ? (
                            <div>
                                <div className="proof-title" style={{ marginTop: "0.5rem" }}>
                                    <OwLabel title="audit_prior_signatures" /> ({stop.prior_signatures.length})
                                </div>
                                <div className="prior-signatures">
                                    {stop.prior_signatures.map((sig) => (
                                        <figure key={sig.guid}>
                                            {sig.signed_url ? <img src={sig.signed_url} alt="Prior signature" /> : null}
                                            <figcaption>{formatAuditTime(sig.captured_at || sig.uploaded_at, tz)}</figcaption>
                                        </figure>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {options.showPhotos ? (
                        <div className="proof-section">
                            <div className="proof-title">
                                <OwLabel title="photos" /> ({stop.photos.length})
                            </div>
                            {stop.photos.length ? (
                                <div className="photo-grid">
                                    {stop.photos.map((photo, index) => (
                                        <figure key={photo.guid}>
                                            {photo.signed_url ? <img src={photo.signed_url} alt={`Photo ${index + 1}`} /> : null}
                                            <figcaption>
                                                <AttachmentTime attachment={photo} timezone={tz} />
                                            </figcaption>
                                        </figure>
                                    ))}
                                </div>
                            ) : (
                                <div className="proof-missing">
                                    <OwLabel title="audit_no_photos_captured" />
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            </div>

            {internal && options.showEvents ? (
                <div className="proof-section">
                    <div className="proof-title">
                        <OwLabel title="audit_event_timeline" /> ({stop.events.length})
                    </div>
                    <EventsTable events={stop.events} timezone={tz} metadataFormatter={metadataFormatter} />
                </div>
            ) : null}
        </div>
    );
};

// ------------------------------------------------------------------ page

/**
 * Daily Route Audit Report page (`/routes/daily/:guid/audit`). Loads the audit payload for one
 * daily route and renders it as a printable document; `?stop=<id>` narrows it to a single
 * stop and `?customer_copy=1` hides internal notes, flags and the event log.
 */
export const DailyRouteAuditReport = () => {
    const { guid } = useParams<{ guid: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { userModel, supplierModel } = useAppContextNonNull();
    const uiContext = useContext(UiContext);
    const intl = useIntl();
    const googleMaps = useGoogleMaps({});
    const apiKey = googleMaps.getGoogleMapsAPIKey();

    const [audit, setAudit] = useState<IOnGetDailyRouteAuditDTO | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState<AuditCsvType | null>(null);

    const stopParam = searchParams.get("stop");
    const stopId = stopParam ? parseInt(stopParam, 10) : null;

    const [options, setOptions] = useState<IViewOptions>(() => ({
        ...DEFAULT_VIEW_OPTIONS,
        customerCopy: searchParams.get("customer_copy") === "1",
    }));

    const notify = (message: string, level: NotificationLevelEnum) => {
        const notification: INotification = { message, position: NotificationPositionEnum.bc, level };
        uiContext.notifyFn(null, notification);
    };

    const fetchAudit = useCallback(async () => {
        if (!guid) return;

        setIsLoading(true);
        setError(null);

        try {
            const url = buildRestUrl({ endpoint: RestEndpointsEnum.DailyRouteAudit, params: { dailyRouteGuid: guid } });
            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });
            const response = await fetch(url, { method: "GET", headers });

            if (!response.ok) {
                throw new Error(`Failed to fetch audit report: ${response.statusText}`);
            }

            const data = await response.json();
            setAudit(data.audit || null);
        } catch (err) {
            setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setIsLoading(false);
        }
    }, [guid, userModel, supplierModel]);

    useEffect(() => {
        fetchAudit();
    }, [fetchAudit]);

    const downloadCsv = async (type: AuditCsvType) => {
        if (!guid) return;

        setDownloading(type);

        try {
            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.DailyRouteAuditCSV,
                params: { dailyRouteGuid: guid },
                query: { type },
            });
            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });
            const response = await fetch(url, { method: "GET", headers });

            if (!response.ok) {
                throw new Error("Failed to download CSV");
            }

            const data: IBaseOnGetCsvDataDTO = await response.json();
            UIService.downloadCSVData(data.csvData ?? "", data.filename || `daily-route-audit-${type}.csv`);
        } catch (err) {
            console.error("Audit CSV download error:", err);
            notify("failed_to_download_csv", NotificationLevelEnum.error);
        } finally {
            setDownloading(null);
        }
    };

    const toggleOption = (key: keyof IViewOptions) => {
        // Keep the state updater pure: the URL write happens outside it.
        const nextValue = !options[key];

        setOptions((prev) => ({ ...prev, [key]: nextValue }));

        if (key === "customerCopy") {
            const params = new URLSearchParams(searchParams);
            if (nextValue) {
                params.set("customer_copy", "1");
            } else {
                params.delete("customer_copy");
            }
            setSearchParams(params, { replace: true });
        }
    };

    // Event metadata is rendered through the app's translations and the supplier's
    // currency rather than fixed English fragments with a "$" prefix.
    const currency = (supplierModel.currency_id && CurrenciesEnum[supplierModel.currency_id]) || "USD";
    const metadataFormatter = useMemo<IEventMetadataFormatter>(
        () => ({
            t: (key, values) => intl.formatMessage({ id: key, defaultMessage: key }, values),
            formatAmount: (value) => HelperService.formatCurrency({ value, currency }),
        }),
        [intl, currency]
    );

    const singleStop = useMemo(() => (audit ? findStop(audit, stopId) : null), [audit, stopId]);
    const stopLookup = useMemo(() => new Map((audit?.stops || []).map((s) => [s.stop_id, s])), [audit]);
    const timeline = useMemo(() => (audit ? sortedRouteTimeline(audit) : []), [audit]);

    if (isLoading) {
        return (
            <div className="DailyRouteAuditReport">
                <div className="loading-state">
                    <i className="fas fa-spinner fa-spin fa-2x mb-3" />
                    <OwLabel title="loading" />
                </div>
            </div>
        );
    }

    if (error || !audit) {
        return (
            <div className="DailyRouteAuditReport">
                <div className="error-state">
                    <i className="fas fa-exclamation-triangle fa-2x mb-3" />
                    <p>{error || intl.formatMessage({ id: "route_not_found", defaultMessage: "Route not found" })}</p>
                    <Link to="/routes/daily" className="btn btn-secondary">
                        <OwLabel title="daily_routes" />
                    </Link>
                </div>
            </div>
        );
    }

    const tz = audit.timezone;
    const route = audit.route;
    const summary = audit.summary;
    const stopsToRender = singleStop ? [singleStop] : audit.stops;
    const internal = !options.customerCopy;
    const printStopPerPage = options.showPhotos && stopsToRender.some((s) => s.photos.length > 0);
    const routeLabel = route.name || route.route_name || "";
    const reportTitle = singleStop
        ? intl.formatMessage({ id: "audit_single_stop_report_title", defaultMessage: "Delivery Audit" })
        : intl.formatMessage({ id: "daily_route_audit_report", defaultMessage: "Daily Route Audit Report" });

    return (
        <div className={`DailyRouteAuditReport${printStopPerPage ? " print-stop-per-page" : ""}`}>
            <div className="d-print-none">
                <Breadcrumbs
                    crumbs={[
                        { title: "daily_routes", url: "/routes/daily" },
                        { title: routeLabel || "Route", url: `/routes/daily/${route.guid}/detail`, skipTranslate: true },
                        { title: "audit_report", url: "" },
                    ]}
                />
            </div>

            <div className="audit-toolbar d-print-none">
                <OwPrintButton />
                <OwButton
                    title="audit_download_stops_csv"
                    leftIconClass="fas fa-file-csv"
                    className="btn-secondary"
                    fn={() => downloadCsv("stops")}
                    disabled={downloading !== null}
                />
                <OwButton
                    title="audit_download_events_csv"
                    leftIconClass="fas fa-stream"
                    className="btn-secondary"
                    fn={() => downloadCsv("events")}
                    disabled={downloading !== null}
                />
                <Link to={`/routes/daily/${route.guid}/detail`} className="btn btn-outline-secondary btn-sm">
                    <i className="fas fa-arrow-left me-1" />
                    <OwLabel title="route_detail" />
                </Link>
                <span className="toolbar-spacer" />
                <div className="toolbar-toggles">
                    <label>
                        <input type="checkbox" checked={options.showMaps} onChange={() => toggleOption("showMaps")} />
                        <OwLabel title="audit_show_maps" />
                    </label>
                    <label>
                        <input type="checkbox" checked={options.showPhotos} onChange={() => toggleOption("showPhotos")} />
                        <OwLabel title="audit_show_photos" />
                    </label>
                    <label>
                        <input type="checkbox" checked={options.showOrderLines} onChange={() => toggleOption("showOrderLines")} />
                        <OwLabel title="audit_show_order_lines" />
                    </label>
                    <label>
                        <input
                            type="checkbox"
                            checked={options.showEvents}
                            onChange={() => toggleOption("showEvents")}
                            disabled={options.customerCopy}
                        />
                        <OwLabel title="audit_show_events" />
                    </label>
                    <label>
                        <input type="checkbox" checked={options.customerCopy} onChange={() => toggleOption("customerCopy")} />
                        <OwLabel title="audit_customer_copy" />
                    </label>
                </div>
            </div>

            {stopId !== null && !singleStop ? (
                <div className="single-stop-banner d-print-none">
                    <i className="fas fa-exclamation-triangle" />
                    <OwLabel title="audit_stop_not_found" />
                    <Link to={`/routes/daily/${route.guid}/audit`}>
                        <OwLabel title="audit_view_full_route" />
                    </Link>
                </div>
            ) : null}

            {singleStop ? (
                <div className="single-stop-banner d-print-none">
                    <i className="fas fa-info-circle" />
                    <OwLabel title="audit_single_stop_banner" />
                    <Link to={`/routes/daily/${route.guid}/audit`}>
                        <OwLabel title="audit_view_full_route" />
                    </Link>
                </div>
            ) : null}

            <div className="report-sheet">
                {/* Header */}
                <div className="report-header">
                    <div className="report-title">
                        <OwTitle title={audit.supplier.name || ""} tag="h4" disableTranslation={true} defaultMargins={false} />
                        <OwTitle title={reportTitle} tag="h2" disableTranslation={true} defaultMargins={false} />
                        <div className="report-subtitle">
                            {routeLabel}
                            {route.route_name && route.name ? ` · ${route.route_name}` : ""} ·{" "}
                            {formatAuditTime(`${route.route_date}T12:00:00Z`, "UTC", "dddd, MMMM D, YYYY")}
                            {singleStop
                                ? ` · ${intl.formatMessage({ id: "stop", defaultMessage: "Stop" })} #${singleStop.display_seq}`
                                : ""}
                        </div>
                    </div>
                    <div className="report-meta">
                        <div>
                            <OwLabel title="generated_at" />: {formatAuditTime(audit.generated_at, tz)}
                        </div>
                        <div>
                            <OwLabel title="audit_report_timezone" />: {tz}
                        </div>
                        {options.customerCopy ? (
                            <div>
                                <OwLabel title="audit_customer_copy" />
                            </div>
                        ) : null}
                    </div>
                </div>

                {/* Route facts */}
                <div className="facts-grid">
                    <Fact
                        label="driver"
                        value={route.driver?.name}
                        sub={[route.driver?.phone, internal ? route.driver?.email : null].filter(Boolean).join(" · ")}
                    />
                    <Fact label="vehicle" value={route.vehicle?.name} sub={route.vehicle?.license_plate || undefined} />
                    <Fact
                        label="status"
                        value={<OwLabel title={route.status} />}
                        sub={
                            internal && route.admin_completed_by ? (
                                <>
                                    <OwLabel title="audit_admin_completed_by" />: {route.admin_completed_by}
                                </>
                            ) : undefined
                        }
                    />
                    <Fact label="planned_start_time" value={formatAuditTime(route.planned_start_time, tz)} />
                    <Fact
                        label="route_started"
                        value={formatAuditTime(route.started_at, tz)}
                        sub={formatGeoPoint(route.started_location) || undefined}
                    />
                    <Fact
                        label="route_completed"
                        value={formatAuditTime(route.completed_at, tz)}
                        sub={formatGeoPoint(route.completed_location) || undefined}
                    />
                    <Fact label="audit_route_duration" value={formatMinutes(summary.route_duration_minutes)} />
                    {internal ? (
                        <Fact label="created_by" value={route.created_by} sub={formatAuditTime(route.created_at, tz) || undefined} />
                    ) : null}
                    {route.is_optimized ? (
                        <Fact
                            label="optimized"
                            value={<OwLabel title="yes" />}
                            sub={
                                route.optimized_vehicle_start_time
                                    ? `${formatAuditTime(route.optimized_vehicle_start_time, tz, "h:mm A")} – ${formatAuditTime(route.optimized_vehicle_end_time, tz, "h:mm A z")}`
                                    : undefined
                            }
                        />
                    ) : null}
                </div>

                {/* Summary */}
                {!singleStop ? (
                    <div className="summary-tiles">
                        <div className="tile">
                            <div className="tile-value">{summary.total_stops}</div>
                            <div className="tile-label">
                                <OwLabel title="stops" />
                            </div>
                        </div>
                        <div className="tile tile-ok">
                            <div className="tile-value">{summary.completed}</div>
                            <div className="tile-label">
                                <OwLabel title="completed" />
                            </div>
                        </div>
                        <div className={`tile${summary.failed ? " tile-danger" : ""}`}>
                            <div className="tile-value">{summary.failed}</div>
                            <div className="tile-label">
                                <OwLabel title="failed" />
                            </div>
                        </div>
                        <div className={`tile${summary.skipped ? " tile-warn" : ""}`}>
                            <div className="tile-value">{summary.skipped}</div>
                            <div className="tile-label">
                                <OwLabel title="skipped" />
                            </div>
                        </div>
                        {summary.pending + summary.arrived > 0 ? (
                            <div className="tile tile-warn">
                                <div className="tile-value">{summary.pending + summary.arrived}</div>
                                <div className="tile-label">
                                    <OwLabel title="audit_not_completed" />
                                </div>
                            </div>
                        ) : null}
                        <div className="tile">
                            <div className="tile-value">
                                {summary.with_signature}/{summary.completed}
                            </div>
                            <div className="tile-label">
                                <OwLabel title="audit_signatures" />
                            </div>
                        </div>
                        <div className="tile">
                            <div className="tile-value">
                                {summary.with_photos}/{summary.completed}
                            </div>
                            <div className="tile-label">
                                <OwLabel title="photos" />
                            </div>
                        </div>
                        <div className="tile">
                            <div className="tile-value">
                                {summary.with_gps}/{summary.total_stops}
                            </div>
                            <div className="tile-label">
                                <OwLabel title="audit_gps_coverage" />
                            </div>
                        </div>
                        {internal ? (
                            <div className={`tile${summary.flagged_stops ? " tile-warn" : " tile-ok"}`}>
                                <div className="tile-value">{summary.flagged_stops}</div>
                                <div className="tile-label">
                                    <OwLabel title="audit_flagged_stops" />
                                </div>
                            </div>
                        ) : null}
                        {audit.van_sell_enabled ? (
                            <>
                                <div className="tile">
                                    <div className="tile-value">{summary.van_sell_orders}</div>
                                    <div className="tile-label">
                                        <OwLabel title="van_sell_orders" />
                                    </div>
                                </div>
                                <div className="tile">
                                    <div className="tile-value">{summary.payments_total.toFixed(2)}</div>
                                    <div className="tile-label">
                                        <OwLabel title="audit_payments_collected" /> ({summary.payments_count})
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </div>
                ) : null}

                {/* Route overview map */}
                {!singleStop && options.showMaps ? (
                    <>
                        <div className="section-title">
                            <i className="fas fa-map-marked-alt" />
                            <OwLabel title="audit_route_overview_map" />
                        </div>
                        <AuditStaticMap markers={buildRouteMarkers(audit)} apiKey={apiKey} size="640x360" legend={true} />
                    </>
                ) : null}

                {/* Stops table */}
                {!singleStop ? (
                    <>
                        <div className="section-title">
                            <i className="fas fa-list-ol" />
                            <OwLabel title="stops" />
                            <span className="section-count">({audit.stops.length})</span>
                        </div>
                        <div className="table-scroll">
                            <table className="audit-table stops-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>
                                            <OwLabel title="customer" />
                                        </th>
                                        <th>
                                            <OwLabel title="order" />
                                        </th>
                                        <th>
                                            <OwLabel title="status" />
                                        </th>
                                        <th>
                                            <OwLabel title="arrived" />
                                        </th>
                                        <th>
                                            <OwLabel title="completed" />
                                        </th>
                                        <th className="num">
                                            <OwLabel title="audit_time_on_site" />
                                        </th>
                                        <th className="num">
                                            <OwLabel title="audit_distance_from_customer" />
                                        </th>
                                        <th>
                                            <OwLabel title="proof" />
                                        </th>
                                        {audit.van_sell_enabled ? (
                                            <th className="num">
                                                <OwLabel title="payments" />
                                            </th>
                                        ) : null}
                                        {internal ? (
                                            <th>
                                                <OwLabel title="audit_flags" />
                                            </th>
                                        ) : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    {audit.stops.map((stop) => {
                                        const severe = stop.flags.some(isSevereFlag);
                                        const rowClass = internal && stop.flags.length ? (severe ? "row-severe" : "row-flagged") : "";
                                        const eta = formatAuditTime(stop.estimated_arrival_time, tz, "h:mm A");

                                        return (
                                            <tr key={stop.stop_id} className={rowClass}>
                                                <td>
                                                    <a href={`#stop-${stop.stop_id}`}>{stop.display_seq}</a>
                                                </td>
                                                <td className="wrap">{stop.customer_name || ""}</td>
                                                <td className="wrap">{stop.order?.name || ""}</td>
                                                <td>
                                                    <StatusPill status={stop.status} />
                                                    {stop.status_reason ? <div className="fact-sub">{stop.status_reason}</div> : null}
                                                </td>
                                                <td>
                                                    {orDash(formatAuditTime(stop.arrived_at, tz, "h:mm:ss A"))}
                                                    {eta ? (
                                                        <div className="fact-sub">
                                                            <OwLabel title="audit_eta" /> {eta}
                                                        </div>
                                                    ) : null}
                                                </td>
                                                <td>{orDash(formatAuditTime(stop.completed_at, tz, "h:mm:ss A"))}</td>
                                                <td className="num">{orDash(formatMinutes(stop.dwell_minutes))}</td>
                                                <td className="num">{orDash(formatDistance(stop.distance_from_customer_m))}</td>
                                                <td>
                                                    <div>
                                                        {stop.signature ? (
                                                            <>
                                                                <i className="fas fa-check me-1" />
                                                                {stop.signed_by_name || <OwLabel title="signature" />}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fas fa-times me-1" />
                                                                <OwLabel
                                                                    title={
                                                                        internal ? "audit_flag_no_signature" : "audit_no_signature_captured"
                                                                    }
                                                                />
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="fact-sub">
                                                        {stop.photos.length} <OwLabel title="photos" />
                                                    </div>
                                                </td>
                                                {audit.van_sell_enabled ? (
                                                    <td className="num">{stop.payments.length ? stop.payments_total.toFixed(2) : "—"}</td>
                                                ) : null}
                                                {internal ? (
                                                    <td>
                                                        <FlagBadges flags={stop.flags} />
                                                    </td>
                                                ) : null}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : null}

                {/* Stop detail cards */}
                <div className="section-title">
                    <i className="fas fa-clipboard-check" />
                    <OwLabel title="audit_stop_details" />
                    {!singleStop ? <span className="section-count">({stopsToRender.length})</span> : null}
                </div>
                {stopsToRender.length === 0 ? (
                    <div className="proof-missing">
                        <OwLabel title="no_stops_on_route" />
                    </div>
                ) : (
                    stopsToRender.map((stop) => (
                        <StopCard
                            key={stop.stop_id}
                            stop={stop}
                            audit={audit}
                            options={options}
                            apiKey={apiKey}
                            routeGuid={route.guid}
                            singleStop={!!singleStop}
                            metadataFormatter={metadataFormatter}
                        />
                    ))
                )}

                {/* EOD reconciliation */}
                {audit.van_sell_enabled && audit.eod_reconciliation && !singleStop ? (
                    <>
                        <div className="section-title">
                            <i className="fas fa-cash-register" />
                            <OwLabel title="eod_reconciliation" />
                        </div>
                        <div className="eod-box">
                            <div className="facts-grid">
                                <Fact label="status" value={audit.eod_reconciliation.status || undefined} />
                                <Fact label="submitted_by" value={audit.eod_reconciliation.submitted_by || undefined} />
                                <Fact label="submitted_at" value={formatAuditTime(audit.eod_reconciliation.submitted_at, tz)} />
                                <Fact label="cash_expected" value={audit.eod_reconciliation.cash_expected?.toFixed(2)} />
                                <Fact label="cash_counted" value={audit.eod_reconciliation.cash_counted?.toFixed(2)} />
                                <Fact label="cash_variance" value={audit.eod_reconciliation.cash_variance?.toFixed(2)} />
                                <Fact label="total_variance_qty" value={audit.eod_reconciliation.total_variance_qty ?? undefined} />
                                {audit.eod_reconciliation.note ? <Fact label="note" wide value={audit.eod_reconciliation.note} /> : null}
                            </div>
                            <div className="d-print-none">
                                <Link to={`/routes/daily/${route.guid}/eod`}>
                                    <OwLabel title="eod_reconciliation" /> →
                                </Link>
                            </div>
                        </div>
                    </>
                ) : null}

                {/* Full timeline */}
                {internal && options.showEvents && !singleStop ? (
                    <>
                        <div className="section-title">
                            <i className="fas fa-stream" />
                            <OwLabel title="audit_event_timeline" />
                            <span className="section-count">({timeline.length})</span>
                        </div>
                        <EventsTable events={timeline} timezone={tz} stopLookup={stopLookup} metadataFormatter={metadataFormatter} />
                    </>
                ) : null}

                <div className="report-footer">
                    <span>
                        {audit.supplier.name} · {reportTitle} · {routeLabel} · {route.route_date}
                    </span>
                    <span>
                        <OwLabel title="generated_at" />: {formatAuditTime(audit.generated_at, tz)}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default DailyRouteAuditReport;
